import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { getFromLocalStorage } from '../mock';
import { User, Mail, Phone, Car, Calendar, Wallet, FileText, ArrowLeft } from 'lucide-react';

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      const currentUser = getFromLocalStorage('currentUser');
      if (!currentUser || currentUser.isAdmin) {
        navigate('/login');
        return;
      }

      // Use currentUser directly from backend
      const customerData = {
        id: currentUser.id || currentUser.username,
        username: currentUser.username,
        name: currentUser.full_name,
        email: currentUser.username,
        phone: currentUser.phone_number,
        carPlate: currentUser.car_plate,
        carDetails: currentUser.car_model,
        bonusBalance: currentUser.bonus_balance || 0,
        registeredDate: new Date().toISOString() // Fallback for existing users
      };

      setCustomer(customerData);

      // Fetch jobs from backend
      try {
        const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
        const token = getFromLocalStorage('accessToken');
        
        const jobsResponse = await fetch(`${BACKEND_URL}/api/jobs`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (jobsResponse.ok) {
          const jobsData = await jobsResponse.json();
          setJobs(jobsData.jobs || []);
        } else {
          // Fallback to localStorage
          const allJobs = getFromLocalStorage('jobs') || [];
          const customerJobs = allJobs.filter(j => 
            j.customerId === customerData.id || j.customerId === currentUser.username
          );
          setJobs(customerJobs);
        }
      } catch (error) {
        console.error('Error loading jobs:', error);
        // Fallback to localStorage
        const allJobs = getFromLocalStorage('jobs') || [];
        const customerJobs = allJobs.filter(j => 
          j.customerId === customerData.id || j.customerId === currentUser.username
        );
        setJobs(customerJobs);
      }
    };

    loadData();
  }, [navigate]);

  if (!customer) return null;

  const totalSpent = jobs.filter(j => j.isPaid).reduce((sum, j) => sum + j.totalPrice, 0);
  const totalPaid = jobs.reduce((sum, j) => sum + j.paidAmount, 0);
  const totalRemaining = jobs.reduce((sum, j) => sum + j.remainingBalance, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="mb-6 border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Panele Dön
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Personal Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-2xl font-display tracking-tight flex items-center gap-2">
                  <User className="h-6 w-6 text-brand" />
                  Hesap Bilgilerim
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Art Garage sistemine kayıtlı bilgileriniz
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-900 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-blue-500" />
                      <p className="text-gray-400 text-sm">Ad Soyad</p>
                    </div>
                    <p className="text-white font-semibold text-lg">{customer.name}</p>
                  </div>

                  <div className="bg-gray-900 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4 text-blue-500" />
                      <p className="text-gray-400 text-sm">Email</p>
                    </div>
                    <p className="text-white font-semibold text-lg">{customer.email}</p>
                  </div>

                  <div className="bg-gray-900 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="h-4 w-4 text-blue-500" />
                      <p className="text-gray-400 text-sm">Telefon</p>
                    </div>
                    <p className="text-white font-semibold text-lg">{customer.phone}</p>
                  </div>

                  <div className="bg-gray-900 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <p className="text-gray-400 text-sm">Kayıt Tarihi</p>
                    </div>
                    <p className="text-white font-semibold text-lg">
                      {new Date(customer.registeredDate).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-900 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Car className="h-4 w-4 text-brand" />
                    <p className="text-gray-400 text-sm">Araç Bilgileri</p>
                  </div>
                  <p className="text-brand font-bold text-xl">{customer.carPlate}</p>
                  <p className="text-white text-lg mt-1">{customer.carDetails}</p>
                </div>
              </CardContent>
            </Card>

            {/* Jobs Summary */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-2xl font-display tracking-tight flex items-center gap-2">
                  <FileText className="h-6 w-6 text-brand" />
                  İşlem Özetim
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Tüm işlemlerinizin detaylı özeti
                </CardDescription>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <div className="text-center py-8">
                    <Car className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Henüz işlem kaydınız bulunmuyor</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobs.map((job, index) => (
                      <div key={job.id} className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={`
                                ${job.status === 'completed' ? 'bg-gray-700' : ''}
                                ${job.status === 'ready' ? 'bg-green-500' : ''}
                                ${job.status === 'in_progress' ? 'bg-blue-500' : ''}
                                ${job.status === 'pending' ? 'bg-gray-500' : ''}
                                text-white
                              `}>
                                {job.status === 'completed' && 'Tamamlandı'}
                                {job.status === 'ready' && 'Hazır'}
                                {job.status === 'in_progress' && 'İşlemde'}
                                {job.status === 'pending' && 'Beklemede'}
                              </Badge>
                              <span className="text-gray-400 text-sm">
                                {new Date(job.startDate).toLocaleDateString('tr-TR')}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {job.services.map((service, idx) => (
                                <Badge key={`svc-${service}-${idx}`} variant="outline" className="border-brand text-brand">
                                  {service}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-white font-bold text-xl">{job.totalPrice} ₺</p>
                            {job.remainingBalance > 0 && (
                              <p className="text-red-500 text-sm">Kalan: {job.remainingBalance} ₺</p>
                            )}
                          </div>
                        </div>

                        {/* Service Details Table */}
                        {job.serviceDetails && job.serviceDetails.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-700">
                            <p className="text-gray-400 text-xs mb-3 font-semibold">Uygulama Detayları:</p>
                            <div className="space-y-3">
                              {job.serviceDetails.map((service, idx) => (
                                <div key={service.name || `sd-${idx}`} className="bg-gray-800 p-3 rounded-lg">
                                  <div className="flex items-start justify-between mb-2">
                                    <p className="text-brand font-semibold">{service.name}</p>
                                    {service.warrantyYears && (
                                      <Badge className="bg-green-600 text-white">
                                        {service.warrantyYears} Yıl Garanti
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 mb-2">
                                    {service.installationDate ? (
                                      <div>
                                        <p className="text-gray-400 text-xs">Yapım Tarihi</p>
                                        <p className="text-white text-sm font-semibold">
                                          {new Date(service.installationDate).toLocaleDateString('tr-TR')}
                                        </p>
                                      </div>
                                    ) : (
                                      <div>
                                        <p className="text-gray-400 text-xs">Yapım Tarihi</p>
                                        <p className="text-yellow-500 text-sm font-semibold">Henüz yapılmadı</p>
                                      </div>
                                    )}
                                    {service.installationDate && service.warrantyYears && (
                                      <div>
                                        <p className="text-gray-400 text-xs">Garanti Bitiş</p>
                                        <p className="text-green-500 text-sm font-semibold">
                                          {(() => {
                                            const endDate = new Date(service.installationDate);
                                            endDate.setFullYear(endDate.getFullYear() + parseInt(service.warrantyYears));
                                            return endDate.toLocaleDateString('tr-TR');
                                          })()}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                  {service.notes && (
                                    <p className="text-gray-300 text-xs mt-2 bg-gray-900 p-2 rounded">
                                      {service.notes}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Payment Summary */}
                        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-700">
                          <div>
                            <p className="text-gray-400 text-xs">Ödenen</p>
                            <p className="text-green-500 text-sm font-semibold">{job.paidAmount} ₺</p>
                          </div>
                          {job.bonusEarned > 0 && (
                            <div>
                              <p className="text-gray-400 text-xs">Kazanılan Bonus</p>
                              <p className="text-green-500 text-sm font-semibold">+{job.bonusEarned} ₺</p>
                            </div>
                          )}
                        </div>

                        {/* Job Notes */}
                        {job.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-700">
                            <p className="text-gray-400 text-xs mb-1">İşlem Notları:</p>
                            <p className="text-gray-300 text-sm bg-gray-800 p-3 rounded">{job.notes}</p>
                          </div>
                        )}

                        {/* Payment History */}
                        {job.payments && job.payments.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-700">
                            <p className="text-gray-400 text-xs mb-2">Ödeme Geçmişi:</p>
                            <div className="space-y-1">
                              {job.payments.map((payment, idx) => (
                                <div key={payment.id || `pay-${payment.date}-${idx}`} className="flex justify-between text-sm bg-gray-800 p-2 rounded">
                                  <span className="text-gray-300">
                                    {new Date(payment.date).toLocaleDateString('tr-TR')} - {payment.note}
                                  </span>
                                  <span className="text-green-500 font-semibold">{payment.amount} ₺</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Stats */}
          <div className="space-y-6">
            {/* Bonus Balance */}
            <Card className="bg-gradient-to-br from-brand to-brand border-0">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Wallet className="h-8 w-8 text-white" />
                  <p className="text-white/80">Bonus Bakiyem</p>
                </div>
                <p className="text-white font-bold text-5xl">{customer.bonusBalance} ₺</p>
                <p className="text-white/80 text-sm mt-2">Gelecek işlemlerinizde kullanabilirsiniz</p>
              </CardContent>
            </Card>

            {/* Financial Summary */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white font-display">Finansal Özet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-gray-400 text-sm">Toplam Harcama</p>
                  <p className="text-white font-bold text-2xl">{totalSpent} ₺</p>
                </div>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-gray-400 text-sm">Toplam Ödenen</p>
                  <p className="text-green-500 font-bold text-2xl">{totalPaid} ₺</p>
                </div>
                {totalRemaining > 0 && (
                  <div className="border-t border-gray-700 pt-4 bg-red-900/20 -m-4 mt-4 p-4 rounded-b-lg">
                    <p className="text-red-400 text-sm">⚠️ Kalan Borç</p>
                    <p className="text-red-500 font-bold text-3xl">{totalRemaining} ₺</p>
                    <p className="text-red-400 text-xs mt-1">Lütfen en kısa sürede ödeme yapınız</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Jobs Stats */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white font-display">İşlem İstatistikleri</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Toplam İşlem</span>
                  <span className="text-white font-bold text-xl">{jobs.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Tamamlanan</span>
                  <span className="text-green-500 font-bold text-xl">
                    {jobs.filter(j => j.status === 'completed').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Devam Eden</span>
                  <span className="text-blue-500 font-bold text-xl">
                    {jobs.filter(j => j.status === 'in_progress' || j.status === 'pending').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Hazır</span>
                  <span className="text-brand font-bold text-xl">
                    {jobs.filter(j => j.status === 'ready').length}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
